import React, { useState, forwardRef, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Input } from '@hopper/legacy_input';
import { Loader } from '@hopper/loading';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function useActiveIndex(options) {
  var _a = useState(null),
      activeIndex = _a[0],
      setActiveIndex = _a[1];

  return [activeIndex, function (keyName) {
    if (activeIndex === null && keyName === "ArrowDown") {
      setActiveIndex(0);
    } else if (keyName === "ArrowDown") {
      if (activeIndex && activeIndex < options.length - 1) {
        setActiveIndex(activeIndex + 1);
      }
    } else if (keyName === "ArrowUp") {
      if (activeIndex && activeIndex > 0) {
        setActiveIndex(activeIndex - 1);
      }
    }
  }, setActiveIndex];
}

var AutocompleteItem = /*#__PURE__*/forwardRef(function (_a, ref) {
  var isActive = _a.isActive,
      children = _a.children,
      otherProps = __rest(_a, ["isActive", "children"]);

  var itemClasses = [{
    "bg-light tw:bg-tw-neutral-100": isActive
  }, "cursor-pointer", "py-xs", "px-md"];
  return /*#__PURE__*/React.createElement("li", __assign({
    className: classnames(itemClasses),
    ref: ref
  }, otherProps), children);
});
AutocompleteItem.propTypes = {
  isActive: PropTypes.bool.isRequired,
  children: PropTypes.node.isRequired
};
AutocompleteItem.displayName = "AutocompleteItem";

var AutocompleteDropdown = function AutocompleteDropdown(_a) {
  var _b;

  var options = _a.options,
      activeItemIndex = _a.activeItemIndex,
      onActiveIndexUpdate = _a.onActiveIndexUpdate,
      offset = _a.offset,
      _c = _a.isOnTop,
      isOnTop = _c === void 0 ? false : _c,
      onSelect = _a.onSelect,
      maxHeight = _a.maxHeight,
      _d = _a.isLoading,
      isLoading = _d === void 0 ? false : _d;
  var itemRefs = [];
  var listRef = useRef(null);
  var listClasses = ["list-reset", "absolute", "pin-l", "w-full", "overflow-y-auto", "bg-lightest tw:bg-white", "border", "border-solid", "border-muted tw:border-tw-neutral-200", "z-50", {
    "border-t-0": !isOnTop
  }, {
    "border-b-0": isOnTop
  }, {
    "rounded-b-base": !isOnTop
  }, {
    "rounded-t-base": isOnTop
  }, {
    "shadow-md": !isOnTop
  }, {
    "shadow-md:top": isOnTop
  }];
  useEffect(function scrollList() {
    var activeItemRef = activeItemIndex ? itemRefs[activeItemIndex] : null;

    if (activeItemRef && listRef.current) {
      if (isActiveItemAboveVisibleArea(activeItemRef, listRef)) {
        scrollUp(listRef, activeItemRef);
      } else if (isActiveItemBelowVisibleArea(activeItemRef, listRef)) {
        scrollDown(listRef, activeItemRef);
      }
    }
  });
  return /*#__PURE__*/React.createElement("ul", {
    ref: listRef,
    className: classnames(listClasses),
    style: (_b = {}, _b[isOnTop ? "bottom" : "top"] = offset, _b.maxHeight = maxHeight, _b)
  }, isLoading && /*#__PURE__*/React.createElement("li", {
    className: "relative",
    style: {
      height: "1.625rem"
    }
  }, /*#__PURE__*/React.createElement(Loader, {
    spinnerSize: 26
  })), options === null || options === void 0 ? void 0 : options.map(function (item, index) {
    return /*#__PURE__*/React.createElement(AutocompleteItem, {
      ref: function ref(itemRef) {
        return itemRefs[index] = itemRef;
      },
      onMouseEnter: function onMouseEnter() {
        return onActiveIndexUpdate(index);
      },
      onMouseLeave: function onMouseLeave() {
        return onActiveIndexUpdate(null);
      },
      key: item.value,
      onClick: function onClick() {
        return onSelect(item);
      },
      isActive: activeItemIndex === index,
      role: "button"
    }, item.label);
  }));
};

function isActiveItemAboveVisibleArea(activeItemRef, listRef) {
  if (listRef.current) {
    return activeItemRef.offsetTop + (activeItemRef.offsetHeight - 10) < listRef.current.scrollTop;
  }
}

function scrollUp(listRef, activeItemRef) {
  if (listRef.current) {
    listRef.current.scrollTop -= activeItemRef.offsetHeight;
  }
}

function isActiveItemBelowVisibleArea(activeItemRef, listRef) {
  if (listRef.current) {
    return activeItemRef.offsetTop > listRef.current.scrollTop + (listRef.current.offsetHeight - 10);
  }
}

function scrollDown(listRef, activeItemRef) {
  if (listRef.current) {
    listRef.current.scrollTop += activeItemRef.offsetHeight;
  }
}

AutocompleteDropdown.propTypes = {
  isOnTop: PropTypes.bool,
  offset: PropTypes.number,
  activeItemIndex: PropTypes.number,
  onActiveIndexUpdate: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  maxHeight: PropTypes.number.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string
  })).isRequired,
  isLoading: PropTypes.bool
};
AutocompleteDropdown.displayName = "AutocompleteDropdown";

var Autocomplete = function Autocomplete(_a) {
  var _b = _a.input,
      InputType = _b === void 0 ? Input : _b,
      _c = _a.options,
      options = _c === void 0 ? [] : _c,
      _d = _a.onSelect,
      onSelect = _d === void 0 ? function () {} : _d,
      _e = _a.onOutsideClick,
      onOutsideClick = _e === void 0 ? function () {} : _e,
      _f = _a.onChange,
      onChange = _f === void 0 ? function () {} : _f,
      value = _a.value,
      className = _a.className,
      _g = _a.inputProps,
      inputProps = _g === void 0 ? {} : _g,
      _h = _a.dropdownMaxHeight,
      dropdownMaxHeight = _h === void 0 ? 7.5 * 16 : _h,
      _j = _a.isLoading,
      isLoading = _j === void 0 ? false : _j;

  var _k = useState(null),
      dropdownOffset = _k[0],
      setDropdownOffset = _k[1];

  var _l = useActiveIndex(options),
      activeIndex = _l[0],
      updateActiveIndex = _l[1],
      setActiveIndex = _l[2];

  var _m = useState(false),
      isDropdownOnTop = _m[0],
      setDropdownOnTop = _m[1];

  var containerRef = useRef(null);
  var inputRef = useRef(null);

  var onKeyDown = inputProps.onKeyDown,
      otherInputProps = __rest(inputProps, ["onKeyDown"]);

  var isShowingDropdown = !!options.length || isLoading;
  var inputClasses = [{
    "rounded-b-none": !isDropdownOnTop && isShowingDropdown
  }, {
    "rounded-t-none": isDropdownOnTop && isShowingDropdown
  }, "resize-none", "block", "w-full"];

  var handleKeyDown = function handleKeyDown(event) {
    if (event.key === "Enter") {
      var activeItem = activeIndex ? options[activeIndex] : null;

      if (activeItem) {
        event.preventDefault();
        onSelect(activeItem);
      }

      setActiveIndex(null);
    } else {
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault();
      }

      updateActiveIndex(event.key);
    }

    onKeyDown && onKeyDown(event);
  };

  useEffect(function setupOutsideClickHandling() {
    var handleGlobalClick = function handleGlobalClick(event) {
      if (containerRef.current && event.target instanceof Node && !containerRef.current.contains(event.target)) {
        onOutsideClick();
      }
    };

    document.addEventListener("click", handleGlobalClick);
    return function () {
      document.removeEventListener("click", handleGlobalClick);
    };
  }, []);
  useEffect(function positionDropdownRelativeToInput() {
    if (inputRef.current) {
      if (isEnoughSpaceForDropdownBelowInput(inputRef, dropdownMaxHeight)) {
        setDropdownOffset(inputRef.current.offsetHeight);
        setDropdownOnTop(false);
      } else {
        setDropdownOffset(inputRef.current.offsetHeight);
        setDropdownOnTop(true);
      }
    }
  }, [dropdownMaxHeight]);
  return /*#__PURE__*/React.createElement("div", {
    ref: containerRef,
    className: classnames("relative auto-qa-autocomplete", className)
  }, /*#__PURE__*/React.createElement(InputType, __assign({
    value: value,
    className: classnames(inputClasses),
    ref: inputRef,
    onChange: onChange,
    onKeyDown: handleKeyDown
  }, otherInputProps)), isShowingDropdown && /*#__PURE__*/React.createElement(AutocompleteDropdown, {
    options: options,
    onActiveIndexUpdate: setActiveIndex,
    activeItemIndex: activeIndex,
    offset: dropdownOffset,
    isOnTop: isDropdownOnTop,
    onSelect: onSelect,
    maxHeight: dropdownMaxHeight,
    isLoading: isLoading
  }));
};

function isEnoughSpaceForDropdownBelowInput(inputRef, dropdownMaxHeight) {
  var pixelsToDeduct = inputRef.current ? inputRef.current.getBoundingClientRect().bottom : 0;
  var pixelsLeftBelowInput = window.innerHeight - pixelsToDeduct;
  return pixelsLeftBelowInput > dropdownMaxHeight;
}

Autocomplete.propTypes = {
  input: PropTypes.oneOfType([PropTypes.elementType, PropTypes.node]),
  value: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired
  })),
  onSelect: PropTypes.func,
  onChange: PropTypes.func,
  onOutsideClick: PropTypes.func,
  dropdownMaxHeight: PropTypes.number,
  className: PropTypes.string,
  isLoading: PropTypes.bool,
  inputProps: PropTypes.object
};

export { Autocomplete };
//# sourceMappingURL=index.es.js.map
